<template>
  <div
    id="salesVariantInfoSection"
    class="main-area sales-variants"
    :class="{ 'without-category': !props.isCategorySelected }"
  >
    <PageTop title="Satış ve Varyant Bilgileri" />
    <div class="info-text" v-if="!props.isCategorySelected">
      <Icons name="InfoCircle" color="#7382a0" />
      <p>
        Satış bilgilerini girebilmek için önce <span>kategori</span> seçimi
        yapmalısınız.
      </p>
    </div>
    <div v-else>
      <div class="varyans-area d-flex flex-column">
        <div class="currency-area">
          <div class="currency-title d-flex flex-row">
            <p>Para Birimi</p>
            <TooltipPlugin
              tooltipText="Fiyat, Merkez Bankası günlük döviz kuru üzerinden otomatik hesaplanacaktır."
              :tooltipWidth="250"
            >
              <template #tooltip-btn>
                <Icons name="InfoCircle" color="#B4B4B4" />
              </template>
            </TooltipPlugin>
          </div>
          <div class="currency-wrapper d-flex flex-row">
            <div class="currency d-flex flex-column">
              <RadioButtonGroup
                name="changeApproveStatusModal-radioGroup"
                class="changeApproveStatusModal-radioGroup"
                :options="currencyList"
                @select="handleSelectCurrency($event)"
                isCurrency
              />
            </div>
          </div>
        </div>
        <div class="input-area" v-if="filteredAttributes.attributes.length">
          <template v-for="(item, index) in filteredAttributes.attributes" :key="item.attributeId">
            <div class="dropdown-list">
              <p>{{ item.attributeName }}</p>
              <DropdownSelectable
                :ref="(el) => (variantRefs[index] = el)"
                :dropItems="item.attributeId === searchedItemId ? searchDropItems : mapAttributeValues(item.attributeValues)"
                @selectedItems="
                  (e) => handleRequiredSelection(e, item.attributeName)
                "
                :label="`${item.attributeName} Seç*`"
                notFoundText="Aradığınız değer bulunamadı.<br> Farklı değerlerle tekrar aramayı deneyebilirsiniz."
                :shouldShowEmptyIcon="true"
                :isCustomValue="item.isCustomValue"
                :singleItemTextLength="singleItemTextLength"
                :searchText="item.attributeName + ' Ara'"
                customInputLabel="Yeni Değer Ekle"
                :isSearchBackend="item.searchable"
                :searchDelay="item.searchable ? 0 : 500"
                @changeSearchValue="handleSearch($event, item)"
                :errorText="dropdownErrors[item.attributeName]"
                :isDisabled="item.isSlicer && isLoadedFromProductDetail"
              />
            </div>
          </template>
        </div>
        <div
          class="info-area d-flex flex-row"
          v-if="filteredAttributes.attributes.length"
        >
          <Icons
            name="InfoCircle"
            color="#7382a0"
            v-if="filteredAttributes.attributes.length"
          />
          <p
            class="attributes-info-text"
            v-html="filteredAttributes.infoText"
          ></p>
        </div>

        <div
          class="single-variant-area"
          v-if="
            filteredAttributes.attributes.length &&
            Object.keys(selectedDynamicAttributes).length ===
              filteredAttributes.attributes.length
          "
        >
          <div class="info-block">
            <Button
              link
              class="add-single-variant"
              @click="openAddSingleVariantModal()"
            >
              <img src="@/assets/images/icons/plus-magenta.svg" />
              <span>Tekil Varyant Ekle</span>
            </Button>
            <p>
              Ürünün sadece tek bir varyantını eklemek için bu seçeneği
              kullanabilirsiniz. (Örneğin sadece Kırmızı-S beden)
            </p>
          </div>
          <ul class="variants" v-if="getSingleVariantRows.length">
            <li v-for="item in getSingleVariantRows" :key="item.id">
              <span>{{ item.attributes.map((i) => i.value).join(", ") }}</span>
              <Button
                link
                class="remove-single-variant"
                @click="removeFromTable(item.id)"
              >
                <img src="@/assets/images/icons/icons-close-white.svg" />
              </Button>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="table-area">
      <SingleProductTableWrapper
        ref="productsTable"
        :category="category"
        :attributes="filteredAttributes.attributes"
        :variantRefs="variantRefs"
        @request-upload="openImageModalForRow"
        :selectedCurrency="selectedCurrency"
      ></SingleProductTableWrapper>
    </div>
    <AddSingleVariantModal
      v-if="filteredAttributes.attributes.length"
      ref="addSingleVariantModalRef"
      :isCategorySelected="isCategorySelected"
      @submitSingleVariant="submitSingleVariant"
      :singleItemTextLength="singleItemTextLength"
      :variantAttributes="filteredAttributes.attributes"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { useStore } from "vuex";
import PageTop from "@/components/Page/PageTop.vue";
import SingleProductTableWrapper from "./SingleProductTableWrapper.vue";
import {
  DropdownSelectable,
  Icons,
  TooltipPlugin,
  RadioButtonGroup,
  Button,
} from "@n11/so-ui-components";
import AddSingleVariantModal from "./AddSingleVariantModal.vue";

const props = defineProps({
  isCategorySelected: {
    type: Boolean,
    required: true,
    default: false,
  },
  currencies: {
    type: Object,
    required: true,
    default: () => {},
  },
  category: {
    type: [Number, null],
    required: true,
  },
  categoryAttributes: {
    type: Array,
    required: true,
    default: () => [],
  },
});

let defaultCurrencyList = [
  {
    id: 1,
    name: "₺ (TL)",
    currencyCode: "TRY",
    rate: 1,
    selected: true,
  },
];

const store = useStore();
const addImageModalRef = ref();
const variantRefs = ref([]);
const selectedCurrency = ref(defaultCurrencyList[0]);
const selectedDynamicAttributes = ref({});
const singleItemTextLength = ref(80);
const productsTable = ref(null);
const addSingleVariantModalRef = ref(null);
const searchDropItems = ref(null)
const searchedItemId = ref(null)
const dropdownErrors = ref({});

const pendingTemplateData = ref(null);
const savedSelectedValues = ref({});
const isLoadedFromProductDetail = ref(false);

const imageUploadRowId = computed(
  () => store.getters["products/getSingleProductImageUploadRowId"]
);
const getSingleVariantRows = computed(() =>
  productsTable.value?.getRows.filter((r) => r.isSingle)
);
const currencyList = computed(() => {
  const propList = props.currencies.currencyRates || [];
  const filteredList = propList.map((item, index) => {
    const currencySymbol = item.currency === "USD" ? "$" : "€";
    const formattedName = `${currencySymbol} (${item.currency})`;
    return {
      id: defaultCurrencyList.length + index + 1,
      name: formattedName,
      rate: item.rate,
      currencyCode: item.currency,
    };
  });
  defaultCurrencyList = [...defaultCurrencyList, ...filteredList];
  return defaultCurrencyList;
});

const filteredAttributes = computed(() => {
  const category = props.category;
  const isCategorySelected = props.isCategorySelected;
  if (!category || !isCategorySelected) {
    return {
      attributes: [],
      infoText: null,
    };
  }

  const attributes = props.categoryAttributes.filter(
    (item) => item.isVariant || item.isSlicer
  );
  const dynamicArea = attributes
    .map(
      (item) => `<span style="font-weight: 600">${item.attributeName}</span>`
    )
    .join(" ve ");
  const infoText = `Satış bilgilerini girebilmek için ${dynamicArea} bilgilerini seçmelisiniz.`;

  productsTable.value?.resetTable();
  if (attributes.length === 0) {
    productsTable.value?.addBlankRow();
  }

  if (pendingTemplateData.value && attributes.length > 0) {
    const dataToLoad = pendingTemplateData.value;
    
    setTimeout(async () => {
      pendingTemplateData.value = null;
      await loadTemplateDataInternal(dataToLoad);
    }, 500);
  }

  return {
    attributes: attributes.reverse(),
    infoText,
    category,
  };
});

const handleSearch = async (newValue, item) => {
  searchedItemId.value = item.attributeId
  try {
    if (!newValue || newValue.trim() === '') {
      searchDropItems.value = mapAttributeValues(item.attributeValues);
      return;
    }

    const searchResult = await store.dispatch("categories/searchAttributeValue", {
      attributeId: item.attributeId, // 449
      categoryId: item.categoryId, // 1002184
      value: newValue
    });
    const searchItems = searchResult.data.valueContents?.map(({value, valueId}) => ({id: valueId, name: value}));
    searchDropItems.value = searchItems;
  }
  catch(err) {
    console.warn(err);
    searchDropItems.value = mapAttributeValues(item.attributeValues);
  }
}

watch(imageUploadRowId, (id) => {
  if (id) {
    addImageModalRef.value?.openAddImageModal();
  }
});

const openImageModalForRow = (rowId) =>
  store.commit("products/SET_SINGLE_PRODUCT_IMAGE_UPLOAD_ROW_ID", rowId);
const handleSelectCurrency = (e) => {
  selectedCurrency.value = e;
};
const mapAttributeValues = (values) =>
  values.map(({ id, value }) => ({ id, name: value, selected: false }));
const handleRequiredSelection = (e, attribute) => {
  const dynamicAttributes = filteredAttributes?.value?.attributes;
  if (dynamicAttributes.length > 0) {
    delete selectedDynamicAttributes.value[attribute];

    if (e.length) {
      selectedDynamicAttributes.value[attribute] = e;
    }

    if (e.length > 0 && dropdownErrors.value[attribute]) {
      const newErrors = { ...dropdownErrors.value };
      delete newErrors[attribute];
      dropdownErrors.value = newErrors;
    }

    const dynamicAttributeList = dynamicAttributes
      .map((d) => d.attributeName)
      .sort();
    const selectedAttributeList = Object.keys(
      selectedDynamicAttributes.value
    ).sort();

    let combinations = [];
    if (
      JSON.stringify(selectedAttributeList) ===
      JSON.stringify(dynamicAttributeList)
    ) {
      const keys = Object.keys(selectedDynamicAttributes.value);
      const values = keys.map((key) => selectedDynamicAttributes.value[key]);

      const cartesian = (arrays, prefix = []) => {
        if (arrays.length === 0) return [prefix];

        const [first, ...rest] = arrays;
        return first.flatMap((item) => cartesian(rest, [...prefix, item]));
      };

      combinations = cartesian(values).map((comb) => {
        return comb.map((item, index) => ({
          name: keys[index],
          value: item.name,
          id: item.id,
        }));
      });
    }

    productsTable.value?.updateCombinations(combinations, false);
  }
};

const submitSingleVariant = (variant) =>
  productsTable.value?.addRow(variant, true);
const removeFromTable = (id) => productsTable.value?.removeRow(id);
const openAddSingleVariantModal = () =>
  addSingleVariantModalRef.value?.openModal();

const checkRowIsValid = (row) => {
  const excepts = [
    "id",
    "images",
    "imagesRowSpan",
    "attributes",
    "hashPlain",
    "order",
    "isSingle",
    "hash",
    "validations",
  ];
  const checks = Object.keys(row)
    .filter((ro) => !excepts.includes(ro))
    .map((r) => productsTable.value?.validateRef(row[r], r, row, true));

  return Promise.all(checks).then((states) => states.every((s) => s));
};

const getSalesVariant = async () => {
  const rows = productsTable.value?.getAllRows || [];
  const validations = await Promise.all(
    rows.map((row) => checkRowIsValid(row))
  );
  const allRowIsValid = rows.length > 0 && validations.every((v) => v);
  const mandatoryAttributes = filteredAttributes.value.attributes.filter((att) => att.isSlicer || att.isMandatory);
  if (Array.isArray(mandatoryAttributes) && mandatoryAttributes.length) {
    for (const item of mandatoryAttributes) {
      const selection = selectedDynamicAttributes.value[item.attributeName];

      if (!selection || selection.length === 0) {
        dropdownErrors.value[item.attributeName] =
          `${item.attributeName} zorunludur.`;
      }
    }
  }
  const allDropdownIsValid = Object.keys(dropdownErrors.value || {}).length === 0 ? true : false;
  const isValid = allRowIsValid && allDropdownIsValid;

  return {
    data: {
      tableRows: rows,
      currency: selectedCurrency.value,
    },
    isValid,
  };
};

const loadTemplateDataInternal = async (templateData) => {
  try {
    if (!templateData || !templateData.attributes || templateData.attributes.length === 0) {
      return;
    }

    const slicerAttributes = templateData.attributes.filter(attr => attr.isSlicer);

    if (slicerAttributes.length === 0) {
      return;
    }

    slicerAttributes.forEach((slicerAttr) => {
      const attributeName = slicerAttr.attributeName;
      const attributeValue = slicerAttr.attributeValue;
      const attributeValueId = slicerAttr.attributeValueId;

      const attribute = filteredAttributes.value.attributes.find(
        attr => attr.attributeName === attributeName
      );

      if (attribute) {
        let selectedValue = null;
        if (attributeValueId) {
          selectedValue = attribute.attributeValues.find(
            val => val.id === attributeValueId
          );
        } else if (attributeValue) {
          selectedValue = attribute.attributeValues.find(
            val => val.value === attributeValue
          );
        }

        if (selectedValue) {
          savedSelectedValues.value[attribute.attributeId] = {
            id: selectedValue.id,
            name: selectedValue.value
          };

          handleRequiredSelection([{
            id: selectedValue.id,
            name: selectedValue.value
          }], attributeName);
        }
      }
    });

    await nextTick();

    slicerAttributes.forEach((slicerAttr) => {
      const attributeName = slicerAttr.attributeName;
      const attributeValue = slicerAttr.attributeValue;
      const attributeValueId = slicerAttr.attributeValueId;

      const attributeIndex = filteredAttributes.value.attributes.findIndex(
        attr => attr.attributeName === attributeName
      );

      if (attributeIndex !== -1 && variantRefs.value[attributeIndex]) {
        const dropdownRef = variantRefs.value[attributeIndex];
        const attribute = filteredAttributes.value.attributes[attributeIndex];

        let selectedValue = null;
        if (attributeValueId) {
          selectedValue = attribute.attributeValues.find(
            val => val.id === attributeValueId
          );
        } else if (attributeValue) {
          selectedValue = attribute.attributeValues.find(
            val => val.value === attributeValue
          );
        }

        if (selectedValue) {
          if (!dropdownRef.isRadioGroup && !dropdownRef.isComboBox) {
            dropdownRef.selectedItems = [{
              id: selectedValue.id,
              name: selectedValue.value
            }];
          }
          else {
            dropdownRef.selectedRadioItem = {
              id: selectedValue.id,
              name: selectedValue.value
            };
          }
        }
      }
    });
  } catch (error) {
    console.error('SalesVariants loadTemplateDataInternal hatası:', error);
  }
};

const loadTemplateData = async (templateData) => {
  try {
    if (!templateData || !templateData.attributes || templateData.attributes.length === 0) {
      isLoadedFromProductDetail.value = false;
      return;
    }

    const slicerAttributes = templateData.attributes.filter(attr => attr.isSlicer);

    if (slicerAttributes.length === 0) {
      isLoadedFromProductDetail.value = false;
      return;
    }

    isLoadedFromProductDetail.value = true;
    pendingTemplateData.value = templateData;
    
    if (filteredAttributes.value.attributes.length === 0) {
      return;
    }

    await loadTemplateDataInternal(templateData);
  } catch (error) {
    console.error('SalesVariants loadTemplateData hatası:', error);
    isLoadedFromProductDetail.value = false;
  }
};

defineExpose({
  getSalesVariant,
  loadTemplateData,
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/utilities.scss";

.main-area {
  width: 100%;
  display: flex;
  gap: 16px;
  padding: 24px;
  border: 1px solid $colorD20;
  border-radius: 8px;
  flex-direction: column;
  background: #fff;

  &.without-category {
    background: $colorD05 !important;
  }

  .input-area {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;

    & > div {
      height: max-content;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .input-with-title {
      gap: 12px;
      font-size: 14px;
      line-height: 18px;
      font-weight: 600;
      color: $colorN80;
    }

    .dropdown-list {
      font-size: 14px;
      line-height: 18px;
      font-weight: 600;
      color: $colorN80;
    }
  }

  .info-text {
    display: flex;
    align-items: center;
    gap: 8px;
    color: $colorD50;
    font-size: 13px;
    line-height: 18px;

    span {
      font-weight: 600;
    }
  }

  .varyans-area {
    gap: 24px;

    .currency-area {
      display: flex;
      flex-direction: row;
      gap: 24px;
    }

    .currency-title {
      align-items: flex-start;
      gap: 4px;
      font-size: 14px;
      line-height: 18px;
      color: $colorN80;
      font-weight: 600;
    }

    .currency-wrapper {
      gap: 36px;
      font-size: 12px;
      line-height: 16px;

      .currency {
        align-items: center;
        gap: 8px;

        label {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 8px;
        }

        span {
          font-size: 10px;
          line-height: 12px;
          font-weight: 400;
          color: $colorD40;
        }
      }
    }

    .info-area {
      gap: 8px;
      font-size: 13px;
      line-height: 18px;
      color: $colorD40;

      span {
        color: $colorN80;
      }
    }
  }

  .single-variant-area {
    background-color: $colorM05;
    padding: 16px;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .info-block {
      display: flex;
      align-items: center;
      gap: 24px;

      p {
        font-weight: 400;
        font-size: 13px;
        line-height: 18px;
        letter-spacing: -0.05px;
        position: relative;

        &:before {
          height: 12px;
          width: 1px;
          background-color: $colorD30;
          content: "";
          position: absolute;
          left: -13px;
          top: 50%;
          margin-top: -6px;
        }
      }

      .add-single-variant {
        color: $colorM50;
        font-family: Open Sans;
        font-weight: 600;
        font-size: 14px;
        line-height: 18px;
        letter-spacing: -0.05px;
        text-decoration: none;
        padding: 0;
        display: flex;
        align-items: center;
        gap: 10px;

        img {
          width: 12px;
          height: 12px;
        }
      }
    }

    .variants {
      display: flex;
      flex-flow: wrap;
      gap: 4px;

      li {
        border: 1px solid $colorN40;
        border-radius: 20px;
        background-color: $colorN00;
        padding: 4px 14px;
        display: inline-flex;
        align-items: center;
        gap: 4px;

        font-family: Open Sans;
        font-weight: 400;
        font-size: 13px;
        line-height: 18px;
        letter-spacing: -0.05px;
      }

      .remove-single-variant {
        color: $colorM50;
        font-family: Open Sans;
        font-weight: 600;
        font-size: 14px;
        line-height: 18px;
        letter-spacing: -0.05px;
        text-decoration: none;
        background-color: $colorN30;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        transition: background-color ease-in-out 150ms;

        &:hover {
          background-color: $colorN70;
        }

        img {
          width: 16px;
        }
      }
    }
  }
}
</style>
